import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Lock } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import SolarAuth, { CUSTOMER_TYPES } from "@/components/solar/SolarAuth";
import ApplianceStep from "@/components/solar/ApplianceStep";
import { StatCard } from "@/components/solar/ReportView";
import { api, authHeaders, getToken, money, formatApiError } from "@/lib/solarApi";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STEPS = ["Customer", "Appliances", "Energy Profile", "Review", "Payment", "Report"];
const fieldCls = "h-12 bg-white border-slate-300 text-slate-900 focus-visible:ring-emerald-600";

const ASSUMPTION_FIELDS = [
  { key: "peak_sun_hours", label: "Peak sun hours / day", hint: "Average strong-sunlight hours at your location" },
  { key: "system_efficiency", label: "System efficiency (0–1)", hint: "Losses from wiring, temperature and dust" },
  { key: "design_margin", label: "Design margin (e.g. 1.25)", hint: "Safety headroom added to sizing" },
  { key: "autonomy_days", label: "Backup days (autonomy)", hint: "How long batteries power essentials without solar input" },
  { key: "simultaneous_factor", label: "Simultaneous-use factor (0–1)", hint: "Share of appliances likely on at once" },
  { key: "voltage_drop_pct", label: "Allowed voltage drop %", hint: "Used for preliminary cable sizing" },
];

function Progress({ step }) {
  return (
    <ol data-testid="wizard-progress" className="flex flex-wrap items-center gap-2">
      {STEPS.map((label, i) => (
        <li key={label} className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-medium ${
            i < step ? "border-emerald-500 bg-emerald-50 text-emerald-700"
            : i === step ? "border-obsidian bg-obsidian text-white"
            : "border-slate-200 bg-white text-slate-400"
          }`}>
            {i < step ? <Check className="h-3 w-3" aria-hidden="true" /> : null}
            {i + 1}. {label}
          </span>
          {i < STEPS.length - 1 && <span className="hidden sm:block h-px w-4 bg-slate-300" aria-hidden="true" />}
        </li>
      ))}
    </ol>
  );
}

export default function SolarCalculator() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [profile, setProfile] = useState({
    name: "", phone: "", country: "Sierra Leone", city: "", customer_type: "residential",
    project_location: "", budget: "", existing_equipment: "", wants_backup: true,
    system_preference: "hybrid", battery_chemistry: "lithium",
    distances_m: { solar: 10, battery: 2, ac: 5 },
  });
  const [items, setItems] = useState([]);
  const [assumptions, setAssumptions] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [free, setFree] = useState(null);
  const [calcId, setCalcId] = useState(null);
  const [orderInfo, setOrderInfo] = useState(null);

  useEffect(() => {
    api.get("/solar/config").then((r) => {
      setConfig(r.data);
      setAssumptions(r.data.assumptions);
      setProfile((p) => ({
        ...p,
        battery_chemistry: r.data.default_chemistry,
        distances_m: r.data.default_distances_m,
      }));
      try {
        const raw = localStorage.getItem("bnm_engine_prefill");
        if (raw) {
          const prefill = JSON.parse(raw);
          localStorage.removeItem("bnm_engine_prefill");
          if (prefill.items?.length) setItems(prefill.items);
          if (prefill.profile) {
            setProfile((p) => ({
              ...p,
              city: prefill.profile.location || prefill.profile.city || p.city,
              battery_chemistry: prefill.profile.battery_chemistry || p.battery_chemistry,
              distances_m: prefill.profile.distances_m || p.distances_m,
            }));
          }
          toast.success("Your Energy Engine results were carried over");
        }
      } catch { /* ignore bad prefill */ }
    }).catch(() => setError("Could not load the calculator. Please refresh."));
    if (getToken()) {
      api.get("/auth/me", { headers: authHeaders() })
        .then((r) => {
          setUser(r.data);
          setProfile((p) => ({
            ...p,
            name: r.data.name || p.name,
            phone: r.data.phone || p.phone,
            country: r.data.country || p.country,
            city: r.data.city || p.city,
            customer_type: r.data.customer_type || p.customer_type,
          }));
        })
        .catch(() => {})
        .finally(() => setAuthChecked(true));
    } else {
      setAuthChecked(true);
    }
  }, []);

  const clientFree = useMemo(() => {
    if (!items.length || !assumptions) return null;
    const dailyWh = items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.watts) || 0) * (Number(i.hours) || 0) * ((Number(i.days_per_week) || 7) / 7), 0);
    const connected = items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.watts) || 0), 0);
    return { dailyKwh: dailyWh / 1000, peak: connected * assumptions.simultaneous_factor };
  }, [items, assumptions]);

  const invalidItem = items.find(
    (i) => !(Number(i.qty) >= 1) || !(Number(i.watts) > 0) || !(Number(i.hours) >= 0) || Number(i.hours) > 24
  );

  const setP = (k) => (e) => setProfile((p) => ({ ...p, [k]: e.target ? e.target.value : e }));
  const setDist = (k) => (e) => setProfile((p) => ({ ...p, distances_m: { ...p.distances_m, [k]: Number(e.target.value) } }));

  const next = () => { setError(""); setStep((s) => Math.min(s + 1, STEPS.length - 1)); window.__lenis ? window.__lenis.scrollTo(0, { immediate: true }) : window.scrollTo(0, 0); };
  const back = () => { setError(""); setStep((s) => Math.max(s - 1, 0)); };

  const continueCustomer = async () => {
    if (!profile.name.trim() || !profile.country.trim() || !profile.city.trim()) {
      setError("Please complete your name, country and city/town.");
      return;
    }
    try {
      await api.put("/auth/me", {
        name: profile.name, phone: profile.phone, country: profile.country,
        city: profile.city, customer_type: profile.customer_type,
      }, { headers: authHeaders() });
    } catch { /* non-blocking */ }
    next();
  };

  const submitCalculation = async () => {
    if (!items.length) { setError("Please add at least one appliance before continuing."); return; }
    if (invalidItem) { setError(`Check the values for "${invalidItem.name}" — quantities, watts and hours must be positive (hours ≤ 24).`); return; }
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/solar/calculations", {
        profile, appliances: items, assumptions,
      }, { headers: authHeaders() });
      setCalcId(data.id);
      setFree(data.free);
      next();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const createOrder = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/solar/orders", {
        calculation_id: calcId,
        origin_url: window.location.origin,
      }, { headers: authHeaders() });
      if (data.already_paid) {
        navigate(`/solar/report?ref=${data.reference}`);
        return;
      }
      if (data.provider === "stripe" && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setOrderInfo(data);
      next();
      toast.success("Order created", { description: `Reference ${data.reference}` });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!authChecked) return null;

  return (
    <>
      <Seo title="Solar Calculator — BNM Sustainable Energy Limited" description="Calculate your solar system: appliances, energy profile, panels, batteries, inverter, cables and full bill of materials." />
      <section data-testid="calculator-page" className="bg-mist grid-lines-light min-h-screen">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 lg:pt-32 pb-24">
          <div className="text-center max-w-2xl mx-auto">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-emerald-600">BNM Solar Energy Calculator</p>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 text-balance">
              Design your solar system
            </h1>
          </div>

          {!user ? (
            <div className="mt-12"><SolarAuth onSuccess={(u) => { setUser(u); setProfile((p) => ({ ...p, name: u.name || p.name, phone: u.phone || p.phone, country: u.country || p.country, city: u.city || p.city, customer_type: u.customer_type || p.customer_type })); }} /></div>
          ) : !config ? (
            <p className="mt-16 text-center text-sm text-slate-500">Loading calculator…</p>
          ) : (
            <>
              <div className="mt-10"><Progress step={step} /></div>

              <div className="mt-8 rounded-lg border border-slate-200 bg-white p-6 sm:p-10 shadow-sm">
                {error && (
                  <p role="alert" data-testid="wizard-error" className="mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
                )}

                {step === 0 && (
                  <div>
                    <h2 className="font-display text-xl font-bold text-slate-900">About your project</h2>
                    <p className="mt-1 text-sm text-slate-500">Signed in as {user.email}. Your details are saved to your account.</p>
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="wz-name">Full name *</Label>
                        <Input id="wz-name" data-testid="wz-name" className={fieldCls} value={profile.name} onChange={setP("name")} />
                      </div>
                      <div>
                        <Label htmlFor="wz-phone">WhatsApp / phone number</Label>
                        <Input id="wz-phone" data-testid="wz-phone" className={fieldCls} value={profile.phone || ""} onChange={setP("phone")} />
                      </div>
                      <div>
                        <Label htmlFor="wz-country">Country *</Label>
                        <Input id="wz-country" data-testid="wz-country" className={fieldCls} value={profile.country} onChange={setP("country")} />
                      </div>
                      <div>
                        <Label htmlFor="wz-city">City / town *</Label>
                        <Input id="wz-city" data-testid="wz-city" className={fieldCls} value={profile.city} onChange={setP("city")} />
                      </div>
                      <div>
                        <Label htmlFor="wz-type">Customer type</Label>
                        <Select value={profile.customer_type} onValueChange={setP("customer_type")}>
                          <SelectTrigger id="wz-type" data-testid="wz-type" className={fieldCls}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {CUSTOMER_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="wz-pref">System type</Label>
                        <Select value={profile.system_preference} onValueChange={setP("system_preference")}>
                          <SelectTrigger id="wz-pref" data-testid="wz-pref" className={fieldCls}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hybrid">Hybrid (solar + grid + battery)</SelectItem>
                            <SelectItem value="off_grid">Off-grid (solar + battery)</SelectItem>
                            <SelectItem value="backup_only">Backup-focused (battery + inverter)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <button type="button" onClick={() => setShowAdvanced((v) => !v)} data-testid="wz-optional-toggle"
                      className="mt-6 text-sm font-semibold text-emerald-700 hover:text-emerald-900">
                      {showAdvanced ? "− Hide optional details" : "+ Optional: location, budget, existing equipment, backup"}
                    </button>
                    {showAdvanced && (
                      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5 rounded-lg border border-slate-200 bg-mist p-5">
                        <div>
                          <Label htmlFor="wz-loc">Project location</Label>
                          <Input id="wz-loc" data-testid="wz-location" className={fieldCls} value={profile.project_location} onChange={setP("project_location")} />
                        </div>
                        <div>
                          <Label htmlFor="wz-budget">Preferred budget</Label>
                          <Input id="wz-budget" data-testid="wz-budget" className={fieldCls} value={profile.budget} onChange={setP("budget")} />
                        </div>
                        <div className="sm:col-span-2">
                          <Label htmlFor="wz-existing">Existing solar equipment (if any)</Label>
                          <Input id="wz-existing" data-testid="wz-existing" className={fieldCls} value={profile.existing_equipment} onChange={setP("existing_equipment")} />
                        </div>
                        <label className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer min-h-[44px]">
                          <input type="checkbox" checked={profile.wants_backup} data-testid="wz-backup" onChange={(e) => setProfile((p) => ({ ...p, wants_backup: e.target.checked }))} className="h-5 w-5 accent-emerald-600" />
                          I want battery backup power
                        </label>
                        <div>
                          <Label htmlFor="wz-chem">Battery chemistry</Label>
                          <Select value={profile.battery_chemistry} onValueChange={setP("battery_chemistry")}>
                            <SelectTrigger id="wz-chem" data-testid="wz-chemistry" className={fieldCls}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="lithium">Lithium / LiFePO4 (recommended)</SelectItem>
                              <SelectItem value="leadacid">Lead-acid</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {step === 1 && (
                  <div>
                    <h2 className="font-display text-xl font-bold text-slate-900">What do you want to power?</h2>
                    <p className="mt-1 text-sm text-slate-500">Pick appliances — wattages are pre-filled with typical values you can edit.</p>
                    <div className="mt-6">
                      <ApplianceStep library={config.appliances} items={items} setItems={setItems} />
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div>
                    <h2 className="font-display text-xl font-bold text-slate-900">Energy profile & assumptions</h2>
                    <p className="mt-1 text-sm text-slate-500">Transparent engineering assumptions — adjust if you know your conditions.</p>
                    {clientFree && (
                      <div className="mt-6 grid grid-cols-2 lg:grid-cols-3 gap-3">
                        <StatCard label="Estimated daily use" value={`${clientFree.dailyKwh.toFixed(2)} kWh`} hint="Live estimate from your appliance list" />
                        <StatCard label="Peak load" value={`${Math.round(clientFree.peak)} W`} hint="Maximum power needed at the same time" />
                        <StatCard label="Appliances" value={items.length} hint={`${items.filter((i) => i.essential).length} marked essential`} />
                      </div>
                    )}
                    <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {ASSUMPTION_FIELDS.map((f) => (
                        <div key={f.key}>
                          <Label htmlFor={`wz-a-${f.key}`}>{f.label}</Label>
                          <Input id={`wz-a-${f.key}`} type="number" step="any" data-testid={`wz-assumption-${f.key}`} className={fieldCls}
                            value={assumptions[f.key]}
                            onChange={(e) => setAssumptions((a) => ({ ...a, [f.key]: e.target.value === "" ? "" : Number(e.target.value) }))} />
                          <p className="mt-1 text-[11px] text-slate-400">{f.hint}</p>
                        </div>
                      ))}
                    </div>
                    <p className="mt-7 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">Cable distances (approximate)</p>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-5">
                      {[["solar", "Panels → inverter (m)"], ["battery", "Battery → inverter (m)"], ["ac", "Inverter → main board (m)"]].map(([k, label]) => (
                        <div key={k}>
                          <Label htmlFor={`wz-d-${k}`}>{label}</Label>
                          <Input id={`wz-d-${k}`} type="number" min={0.5} max={300} data-testid={`wz-distance-${k}`} className={fieldCls} value={profile.distances_m[k]} onChange={setDist(k)} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {step === 3 && free && (
                  <div>
                    <h2 className="font-display text-xl font-bold text-slate-900">Your free energy estimate</h2>
                    <p className="mt-1 text-sm text-slate-500">Calculation saved to your account.</p>
                    <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <StatCard label="Daily consumption" value={`${free.daily_kwh} kWh`} testid="free-daily" />
                      <StatCard label="Monthly" value={`${free.monthly_kwh} kWh`} testid="free-monthly" />
                      <StatCard label="Peak load" value={`${free.peak_w} W`} testid="free-peak" />
                      <StatCard label="Solar size range" value={free.solar_range} testid="free-range" />
                    </div>
                    <div className="mt-8 rounded-lg border-2 border-emerald-500 bg-emerald-50/50 p-6 sm:p-8">
                      <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-emerald-700">Professional BNM Solar Design</p>
                          <p className="mt-2 font-display text-3xl font-extrabold text-slate-900" data-testid="wizard-price">
                            {money(config.pricing.amount, config.pricing.currency)}
                          </p>
                          <p className="mt-2 max-w-md text-sm text-slate-600">
                            Exact panel count, battery bank, inverter rating, cable sizes, protection equipment, full bill of
                            materials and a branded PDF report — delivered to your dashboard and email.
                          </p>
                        </div>
                        <button
                          onClick={createOrder}
                          disabled={busy}
                          data-testid="unlock-design-button"
                          className="inline-flex items-center gap-2 rounded-md bg-obsidian px-7 py-4 font-semibold text-white transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-pine hover:text-volt disabled:opacity-60"
                        >
                          <Lock className="h-4 w-4" aria-hidden="true" />
                          {busy ? "Creating order…" : "Unlock Full Solar Design"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {step === 4 && orderInfo && (
                  <div data-testid="payment-step">
                    <h2 className="font-display text-xl font-bold text-slate-900">Payment</h2>
                    <div className="mt-6 rounded-lg border border-amber-300 bg-amber-50 p-6">
                      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber-700">Order {orderInfo.reference} — Payment pending</p>
                      <p className="mt-3 text-sm leading-relaxed text-slate-700">{orderInfo.instructions}</p>
                      <p className="mt-4 text-sm text-slate-600">
                        Already paid? Your report unlocks automatically once BNM confirms.{" "}
                        <Link to={`/solar/report?ref=${orderInfo.reference}`} data-testid="check-status-link" className="font-semibold text-emerald-700 hover:underline">
                          Check order status →
                        </Link>
                      </p>
                    </div>
                  </div>
                )}

                <div className="mt-9 flex items-center justify-between gap-4 border-t border-slate-200 pt-6">
                  {step > 0 && step < 4 ? (
                    <button onClick={back} data-testid="wizard-back" className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 hover:border-emerald-400 transition-colors">
                      <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
                    </button>
                  ) : <span />}
                  {step === 0 && (
                    <button onClick={continueCustomer} data-testid="wizard-next" className="inline-flex items-center gap-2 rounded-md bg-obsidian px-6 py-3 font-semibold text-white hover:bg-pine hover:text-volt transition-colors">
                      Continue <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                  {step === 1 && (
                    <button onClick={() => { if (!items.length) { setError("Please add at least one appliance before continuing."); return; } next(); }} data-testid="wizard-next" className="inline-flex items-center gap-2 rounded-md bg-obsidian px-6 py-3 font-semibold text-white hover:bg-pine hover:text-volt transition-colors">
                      Continue <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                  {step === 2 && (
                    <button onClick={submitCalculation} disabled={busy} data-testid="wizard-calculate" className="inline-flex items-center gap-2 rounded-md bg-obsidian px-6 py-3 font-semibold text-white hover:bg-pine hover:text-volt transition-colors disabled:opacity-60">
                      {busy ? "Calculating…" : "Calculate My System"} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                  {step === 3 && (
                    <button onClick={() => setStep(1)} data-testid="wizard-edit-appliances" className="text-sm font-semibold text-emerald-700 hover:underline">
                      Edit appliances
                    </button>
                  )}
                </div>
              </div>

              <p className="mt-6 text-center text-xs text-slate-500 max-w-xl mx-auto">
                Preliminary system recommendation — final system sizing should be verified through a BNM site assessment
                and qualified technical review before installation.
              </p>
            </>
          )}
        </div>
      </section>
    </>
  );
}
